import React, { useCallback, useEffect, useState } from 'react';
import AppLayout from 'containers/layouts/AppLayout';
import { Box, Button, Container, FormControl, FormControlLabel, FormHelperText, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client/react';
import { withApollo } from 'hoc/withApollo';
import ImageUploadField, { FileType, isNewFile, uploadPictures } from 'components/fields/ImageUploadField';
import { useSnackbar } from 'notistack';
import TextInputField from 'components/fields/TextInputField';
import ReCAPTCHA from 'react-google-recaptcha';
import { useRouter } from 'next/router';

const SEND_CONTACT_FORM_EMAIL = gql`
  mutation sendContactFormEmail($contactForm: ContactFormInfos!) {
    sendContactFormEmail(contactForm: $contactForm)
  }
`;

type ContactFields = {
  category: 'message' | 'improvementbug';
  name: string;
  email: string;
  object: string;
  message: string;
  pictures: FileType[];
  website?: string;
  submittedAt: string;
};

const Contact = () => {
  const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;
  const router = useRouter();
  const form = useForm<ContactFields>({
    mode: 'onTouched',
    defaultValues: {
      category: 'message',
      website: '',
      submittedAt: Date.now().toString(),
    }
  });

  const {
    control,
    handleSubmit,
    register,
    watch,
  } = form;

  const isBug = watch('category') === 'improvementbug';
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);

  const [sendContactForm, { data, error, loading }] = useMutation(SEND_CONTACT_FORM_EMAIL);
  const [messageSent, setMessageSent] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (!loading && data) {
      setMessageSent(true);
    } else if (error) {
      enqueueSnackbar("Une erreur s'est produite, merci de bien vouloir réessayer.", {
        preventDuplicate: true,
      });
    }
  }, [data, error, loading]);

  // Pre-fill from chatbot bug/improvement report or from the error boundary.
  useEffect(() => {
    if (router.query.from === 'chatbot') {
      try {
        const report = localStorage.getItem('chatbot_report');
        if (report) {
          const reportData = JSON.parse(report);
          if (reportData.category) form.setValue('category', reportData.category);
          if (reportData.object) form.setValue('object', reportData.object);
          if (reportData.message) form.setValue('message', reportData.message);
          if (reportData.name) form.setValue('name', reportData.name);
          if (reportData.email) form.setValue('email', reportData.email);
          localStorage.removeItem('chatbot_report');
        }
      } catch {
        // localStorage may be unavailable
      }
    }
    if (router.query.from === 'error') {
      try {
        const report = localStorage.getItem('error_report');
        if (report) {
          const reportData = JSON.parse(report);
          form.setValue('category', 'improvementbug');
          if (reportData.object) form.setValue('object', reportData.object);
          if (reportData.message) form.setValue('message', reportData.message);
          localStorage.removeItem('error_report');
        } else {
          form.setValue('category', 'improvementbug');
        }
      } catch {
        form.setValue('category', 'improvementbug');
      }
    }
  }, [router.query.from, form]);

  const onSubmit: SubmitHandler<ContactFields> = useCallback(
    async ({ name, email, object, message, category, pictures, website, submittedAt }) => {
      if (recaptchaSiteKey && !captchaToken) {
        enqueueSnackbar('Merci de valider le captcha avant envoi.', {
          preventDuplicate: true,
        });
        return;
      }

      const newPictures = pictures?.filter(isNewFile) ?? [];
      if (newPictures.length > 0) {
        await uploadPictures(newPictures);
      }

      sendContactForm({
        variables: {
          contactForm: {
            name,
            email,
            object,
            message,
            category,
            pictures: newPictures.map((p) => `/static/images/newUpload/${p.file.name}`),
            captchaToken,
            website,
            submittedAt,
          },
        },
      });
    },
    [captchaToken, enqueueSnackbar, recaptchaSiteKey, sendContactForm],
  );

  if (messageSent) {
    return (
      <AppLayout>
        <Container maxWidth="md" sx={{ marginTop: { xs: 3, md: 6 }, marginBottom: 6 }}>
          <Typography variant="h1" textAlign="center">
            Contact
          </Typography>
          <Typography variant="h6" textAlign="center" sx={{ marginTop: 3 }}>
            Votre message a bien été envoyé. Merci.
          </Typography>
        </Container>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Container maxWidth="md" sx={{ marginTop: { xs: 3, md: 6 }, marginBottom: 6 }}>
        <Typography variant="h1" textAlign="center">
          Contact
        </Typography>

        <FormProvider {...form}>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 3 }}>
            <Stack direction="column" gap={2}>
              <Controller
                name="category"
                control={control}
                rules={{ required: 'Le type de message est requis' }}
                render={({ field, fieldState }) => (
                  <FormControl error={fieldState.invalid} sx={{ margin: 'auto' }}>
                    <RadioGroup row {...field}>
                      <FormControlLabel value="message" control={<Radio />} label="Message" />
                      <FormControlLabel value="improvementbug" control={<Radio />} label="Amélioration ou Correction" />
                    </RadioGroup>
                    <FormHelperText>{fieldState.error?.message}</FormHelperText>
                  </FormControl>
                )}
              />

              <TextInputField
                name="name"
                label="Prénom Nom"
                rules={{
                  required: 'Nom requis',
                }}
              />

              <TextInputField
                name="email"
                label="Adresse de messagerie"
                type="email"
                rules={{
                  required: 'Adresse de messagerie requise',
                }}
              />

              <TextInputField
                name="object"
                label="Objet"
                rules={{
                  required: 'Objet requis',
                }}
              />

              <TextInputField
                name="message"
                label="Message"
                required
                multiline
                minRows={6}
                maxRows={10}
                rules={{
                  required: 'Message requis',
                }}
              />

              <input
                type="text"
                style={{ display: 'none' }}
                tabIndex={-1}
                autoComplete="off"
                {...register('website')}
              />

              <input type="hidden" {...register('submittedAt')} />

              {recaptchaSiteKey && (
                <Box sx={{ margin: 'auto' }}>
                  <ReCAPTCHA
                    sitekey={recaptchaSiteKey}
                    onChange={(token) => setCaptchaToken(token)}
                    onExpired={() => setCaptchaToken(null)}
                    onErrored={() => setCaptchaToken(null)}
                  />
                </Box>
              )}

              {isBug && (
                <ImageUploadField
                  name="pictures"
                  label="Ajoutez une photo de votre ecran"
                  dropzoneText="Déposez ici vos photos pour nous aider à comprendre la correction ou l'amélioration souhaitée (au format jpg et inférieur à 4Mo)"
                  filesLimit={3}
                />
              )}

              <Button
                type="submit"
                variant="contained"
                sx={{ margin: 'auto' }}
                disabled={Boolean(recaptchaSiteKey) && !captchaToken}
              >
                Envoyer
              </Button>
            </Stack>
          </Box>
        </FormProvider>
      </Container>
    </AppLayout>
  );
};

export default withApollo()(Contact);
